import { useContext, useMemo } from "react";

import { useUser } from "@components/user-context";
import {
  makeUniversalBenchmarksAdoptableMetrics,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  getMetrics,
  type SalesMetricV2,
} from "@north-beam/nb-client-side/metrics-v2";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { UniversalBenchmarksContext } from "../context";

interface Args {
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
}

export const useUniversalBenchmarksMetrics = ({
  attributionModel,
  attributionWindow,
  accountingMode,
}: Args) => {
  const { user } = useUser();
  const context = useContext(UniversalBenchmarksContext);

  if (!context) {
    throw new Error(
      "useUniversalBenchmarksMetrics can only be used inside UniversalBenchmarksContext",
    );
  }

  const { adoptedBenchmark } = context;

  return useMemo(() => {
    if (!adoptedBenchmark) {
      return { metricsV1: [], metricsV2: [] };
    }

    const { metrics: availableMetricsV1 } =
      makeUniversalBenchmarksAdoptableMetrics(
        attributionModel,
        accountingMode === "cash" ? "infinity" : attributionWindow,
        accountingMode,
        user as any,
      );

    const availableMetricsV2 = getMetrics({
      user,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity: "daily",
      customGoalRows: [],
      customMetricRows: [],
      showV1Forecasts: false,
    });

    const metricsV1: SalesPageMetric[] = [];
    const metricsV2: SalesMetricV2[] = [];

    adoptedBenchmark.selectedMetricIds.forEach((metricId) => {
      const metricV1 = availableMetricsV1.find(
        (e) => e.metadata.overviewPageMetricId === metricId,
      );

      const metricV2 = availableMetricsV2.find((e) => e.id === metricId);

      if (metricV1) {
        metricsV1.push(metricV1);
      }

      if (metricV2) {
        metricsV2.push(metricV2);
      }
    });

    return { metricsV1, metricsV2 };
  }, [
    attributionModel,
    attributionWindow,
    accountingMode,
    adoptedBenchmark,
    user,
  ]);
};
